import { useEffect, useState } from 'react'
import { Loader2, Save, FileText, BookOpen, Building2, Scale } from 'lucide-react'
import { FormSidemenu, type FormSection } from '@/components/ui/FormSidemenu'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { RichTextEditor } from '@/components/ui/RichTextEditor'
import { FileUpload } from '@/components/ui/FileUpload'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { useAuthority } from '@/hooks/useAuthority'
import {
  ProposalTemplatesService,
  type ProposalTemplate,
  type ProposalSlot,
} from '@/services/modules/ProposalTemplatesService'

const PROPOSAL_SLOTS: { slot: ProposalSlot; title: string; hint: string }[] = [
  { slot: 'cover',      title: 'Capa',       hint: 'Primeira página do PDF da proposta.' },
  { slot: 'back_cover', title: 'Contracapa', hint: 'Última página do PDF da proposta.' },
]

const MANUAL_SLOTS: { slot: ProposalSlot; title: string; hint: string }[] = [
  { slot: 'manual_cover',      title: 'Capa do manual',       hint: 'Primeira página do Manual de Utilização.' },
  { slot: 'manual_back_cover', title: 'Contracapa do manual', hint: 'Última página do Manual de Utilização.' },
]

export function SettingsProposalTemplatesPage() {
  // PUT/POST/DELETE /settings/commercial/proposal-templates usam a mesma permissão.
  const { can } = useAuthority()
  const canManage = can('settings.proposal_templates.manage')
  const [tpl, setTpl] = useState<ProposalTemplate | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Dados da CONTRATADA
  const [name, setName] = useState('')
  const [document, setDocument] = useState('')
  const [address, setAddress] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')

  const [observations, setObservations] = useState('')

  function hydrate(t: ProposalTemplate) {
    setTpl(t)
    setName(t.contractorName ?? '')
    setDocument(t.contractorDocument ?? '')
    setAddress(t.contractorAddress ?? '')
    setPostalCode(t.contractorPostalCode ?? '')
    setCity(t.contractorCity ?? '')
    setState(t.contractorState ?? '')
    setEmail(t.contractorEmail ?? '')
    setPhone(t.contractorPhone ?? '')
    setObservations(t.defaultObservations ?? '')
  }

  useEffect(() => {
    ProposalTemplatesService.get()
      .then(hydrate)
      .catch(() => toast.error('Erro ao carregar as configurações.'))
      .finally(() => setLoading(false))
  }, [])

  async function uploadSlot(slot: ProposalSlot, file: File) {
    try { setTpl(await ProposalTemplatesService.upload(slot, file)); toast.success('Arquivo enviado.') }
    catch (e) { toast.error({ title: 'Erro ao enviar', description: extractApiError(e).message }) }
  }
  async function removeSlot(slot: ProposalSlot) {
    try { setTpl(await ProposalTemplatesService.remove(slot)); toast.success('Arquivo removido.') }
    catch { toast.error('Erro ao remover.') }
  }

  async function save() {
    setSaving(true)
    try {
      const t = await ProposalTemplatesService.update({
        contractorName: name.trim() || null,
        contractorDocument: document.trim() || null,
        contractorAddress: address.trim() || null,
        contractorPostalCode: postalCode.trim() || null,
        contractorCity: city.trim() || null,
        contractorState: state.trim().toUpperCase() || null,
        contractorEmail: email.trim() || null,
        contractorPhone: phone.trim() || null,
        defaultObservations: observations.trim() || null,
      })
      hydrate(t)
      toast.success('Configurações salvas.')
    } catch (e) {
      toast.error({ title: 'Não foi possível salvar', description: extractApiError(e).message })
    } finally { setSaving(false) }
  }

  const slotData = (slot: ProposalSlot) => {
    if (!tpl) return null
    switch (slot) {
      case 'cover': return tpl.cover
      case 'back_cover': return tpl.backCover
      case 'manual_cover': return tpl.manualCover
      case 'manual_back_cover': return tpl.manualBackCover
      default: return null
    }
  }

  const sections: FormSection[] = [
    {
      key: 'capas', label: 'Capas da proposta', icon: FileText,
      content: (
        <Section
          icon={<FileText size={15} />}
          title="Capas da proposta"
          description="O sistema monta o PDF: capa → miolo gerado (benefícios, valores e condições) → contracapa."
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {PROPOSAL_SLOTS.map(({ slot, title, hint }) => {
              const d = slotData(slot)
              return (
                <FileUpload
                  key={slot} accept="pdf" label={title} hint={hint}
                  currentUrl={d?.url ?? null}
                  currentName={d?.present ? `${title}.pdf` : null}
                  onSelect={(f) => uploadSlot(slot, f)}
                  onRemove={canManage ? () => removeSlot(slot) : undefined}
                  disabled={!canManage}
                  maxMB={20}
                />
              )
            })}
          </div>
        </Section>
      ),
    },
    {
      key: 'capas-manual', label: 'Capas do manual', icon: BookOpen,
      content: (
        <Section
          icon={<BookOpen size={15} />}
          title="Capas do manual"
          description="O Manual de Utilização usa estas capas e um miolo gerado a partir dos benefícios inclusos na proposta."
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {MANUAL_SLOTS.map(({ slot, title, hint }) => {
              const d = slotData(slot)
              return (
                <FileUpload
                  key={slot} accept="pdf" label={title} hint={hint}
                  currentUrl={d?.url ?? null}
                  currentName={d?.present ? `${title}.pdf` : null}
                  onSelect={(f) => uploadSlot(slot, f)}
                  onRemove={canManage ? () => removeSlot(slot) : undefined}
                  disabled={!canManage}
                  maxMB={20}
                />
              )
            })}
          </div>
        </Section>
      ),
    },
    {
      key: 'contratada', label: 'Dados da contratada', icon: Building2,
      content: (
        <Section
          icon={<Building2 size={15} />}
          title="Dados da contratada"
          description="Aparecem no bloco “Dados das partes” do PDF, do lado da CONTRATADA."
        >
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Input label="Razão social" value={name} onChange={e => setName(e.target.value)} placeholder="Ampla Benefícios LTDA" wrapperClassName="md:col-span-2" />
            <Input label="CNPJ" value={document} onChange={e => setDocument(e.target.value)} placeholder="00.000.000/0001-00" />
            <Input label="CEP" value={postalCode} onChange={e => setPostalCode(e.target.value)} placeholder="00000-000" />
            <Input label="Endereço" value={address} onChange={e => setAddress(e.target.value)} placeholder="Rua, número, bairro" wrapperClassName="md:col-span-2" />
            <Input label="Cidade" value={city} onChange={e => setCity(e.target.value)} placeholder="Vargem Grande do Sul" />
            <Input label="Estado" value={state} onChange={e => setState(e.target.value)} placeholder="SP" maxLength={2} />
            <Input label="E-mail" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="contato@amplamaisbeneficios.com.br" />
            <Input label="Telefone" value={phone} onChange={e => setPhone(e.target.value)} placeholder="0800 024 4262" />
          </div>
        </Section>
      ),
    },
    {
      key: 'observacoes', label: 'Observações padrão', icon: Scale,
      content: (
        <Section
          icon={<Scale size={15} />}
          title="Observações padrão"
          description="Copiadas para cada nova proposta. Permanência, pagamento e condições contratuais viraram cadastro em Cadastros da proposta."
        >
          <div className="space-y-3">
            <RichTextEditor
              label="Observações e condições comerciais"
              value={observations}
              onChange={setObservations}
              hint="Saem no PDF do jeito que estão aqui. Use uma lista para várias observações."
            />
          </div>
        </Section>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Modelos de proposta"
        subtitle="Capas da proposta e do manual, dados da contratada e observações padrão"
        crumbs={[{ label: 'Configurações', to: '/settings' }, { label: 'Comercial' }, { label: 'Modelos de proposta' }]}
        actions={(
          <Button onClick={save} loading={saving} disabled={!canManage} iconLeft={<Save size={14} />}
            title={canManage ? undefined : 'Sem permissão para alterar os modelos de proposta'}>Salvar</Button>
        )}
      />

      {loading
        ? <div className="flex items-center justify-center py-20"><Loader2 size={28} className="animate-spin text-gray-400" /></div>
        : <FormSidemenu sections={sections} />}
    </>
  )
}

function Section({ icon, title, description, children }: {
  icon: React.ReactNode
  title: string
  description: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-md bg-white p-5 ring-1 ring-gray-200 sm:p-6">
      <h2 className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-gray-800">
        <span className="text-brand-600">{icon}</span>{title}
      </h2>
      <p className="mb-4 text-xs text-gray-500">{description}</p>
      {children}
    </div>
  )
}
